<script lang="ts">
	import type { Report, ExtractedDocument } from '#lib/types';
	import { i18n, t } from '#lib/i18n/state.svelte';
	import { qualityNote } from '#lib/i18n/quality';
	import { categoryLabel, confidenceShort, locale, relativeTime } from '#lib/ui/labels';
	import IssueAppendix from './IssueAppendix.svelte';

	interface Props {
		report: Report;
		doc: ExtractedDocument;
	}
	let { report, doc }: Props = $props();

	const weights = $derived(
		report.scores.categories.map((c) => ({ category: c.category, weight: c.weight }))
	);

	let logOpen = $state(false);
</script>

<div class="method">
	<section class="lead card">
		<h2>{t('method.title')}</h2>
		<p>
			{t('method.leadA')}
			<strong>{t('method.leadStrong')}</strong>
			{t('method.leadB')}
			<em>{t('method.leadEm')}</em>.
		</p>
		<div class="pipeline" aria-label={t('method.pipelineAria')}>
			<span>{t('method.pipe.extract')}</span><span aria-hidden="true">→</span>
			<span>{t('method.pipe.parse')}</span><span aria-hidden="true">→</span>
			<span>{t('method.pipe.cross')}</span><span aria-hidden="true">→</span>
			<span>{t('method.pipe.confidence')}</span><span aria-hidden="true">→</span>
			<span>{t('method.pipe.score')}</span>
		</div>
	</section>

	<div class="grid2">
		<section class="card panel">
			<h3>{t('method.extractTitle')}</h3>
			<ul>
				<li>{t('method.extractPdf')}</li>
				<li>{t('method.extractDocx')}</li>
				<li>{t('method.extractTex')}</li>
			</ul>
			<p class="small muted">
				{t('method.operatorNote')}
				<strong>{t('method.operatorNoteStrong')}</strong>.
			</p>
		</section>

		<section class="card panel">
			<h3>{t('method.confTitle')}</h3>
			<p class="small">
				{t('method.confBody')}
				<strong>{t('method.confBodyStrong')}</strong>.
			</p>
			<table class="conf-table">
				<thead>
					<tr>
						<th scope="col">{t('method.confColLevel')}</th>
						<th scope="col">{t('method.confColCond')}</th>
						<th scope="col">{t('method.confColEffect')}</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td><span class="chip chip-ok">{t('method.confHigh')}</span></td>
						<td>{t('method.confHighCond')}</td>
						<td>{t('method.confHighEffect')}</td>
					</tr>
					<tr>
						<td><span class="chip chip-warning">{t('method.confMedium')}</span></td>
						<td>{t('method.confMediumCond')}</td>
						<td>{t('method.confMediumEffect')}</td>
					</tr>
					<tr>
						<td><span class="chip chip-neutral">{t('method.confLow')}</span></td>
						<td>{t('method.confLowCond')}</td>
						<td>{t('method.confLowEffect')}</td>
					</tr>
				</tbody>
			</table>
			<p class="small muted">{t('method.confNote')}</p>
		</section>
	</div>

	<section class="card panel">
		<h3>{t('method.statsTitle')}</h3>
		<div class="two-col">
			<div>
				<p class="small">
					{t('method.statsBody')}
					<strong>{t('method.statsBodyStrong')}</strong>
					{t('method.statsBodyEnd')}
				</p>
				<ul class="small">
					<li>{t('method.statsFlip')}</li>
					<li>{t('method.statsDev')}</li>
					<li>{t('method.statsNoP')}</li>
					<li>{t('method.statsOp')}</li>
				</ul>
			</div>
			<div class="formulas mono small">
				<p>{t('method.statsT')}</p>
				<p>{t('method.statsF')}</p>
				<p>{t('method.statsChi')}</p>
				<p>{t('method.statsR')}</p>
				<p>{t('method.statsZ')}</p>
			</div>
		</div>
	</section>

	<div class="grid2">
		<section class="card panel">
			<h3>{t('method.refTitle')}</h3>
			<p class="small">{t('method.refBody')}</p>
		</section>

		<section class="card panel">
			<h3>{t('method.langTitle')}</h3>
			<p class="small">
				{t('method.langBodyA')}
				<span class="mono">{t('method.langBodyMonoA')}</span>{t('method.langBodyB')}<span class="mono"
					>{t('method.langBodyMonoB')}</span
				>{t('method.langBodyC')}
				<em>{t('method.langBodyEm')}</em>
				{t('method.langBodyD')}
			</p>
		</section>
	</div>

	<section class="card panel">
		<h3>{t('method.scoreTitle')}</h3>
		<p class="small">{t('method.scoreBody')}</p>
		<div class="weights">
			{#each weights as w (w.category)}
				<div class="weight">
					<span class="w-label">{categoryLabel(w.category)}</span>
					<span class="w-track" aria-hidden="true">
						<span class="w-fill" style:width="{w.weight * 100 * 2.2}%"></span>
					</span>
					<span class="mono">%{Math.round(w.weight * 100)}</span>
				</div>
			{/each}
		</div>
		<p class="small muted">{@html t('method.scoreThresholds')}</p>
	</section>

	<section class="card panel">
		<h3>{t('method.limitsTitle')}</h3>
		<ul class="small limits">
			<li>{@html t('method.limit1')}</li>
			<li>{t('method.limit2')}</li>
			<li>{t('method.limit3')}</li>
			<li>{t('method.limit4')}</li>
			<li>{t('method.limit5')}</li>
		</ul>
	</section>

	<section class="card panel">
		<button class="log-toggle" onclick={() => (logOpen = !logOpen)} aria-expanded={logOpen}>
			<span>{t('method.logToggle')}</span>
			<span class="mono small muted">{relativeTime(report.generatedAt)} · v{report.version}</span>
			<span aria-hidden="true">{logOpen ? '−' : '+'}</span>
		</button>
		{#if logOpen}
			<div class="log">
				<dl>
					<dt>{t('method.logFile')}</dt>
					<dd class="mono">{report.document.fileName}</dd>
					<dt>{t('method.logFormat')}</dt>
					<dd>{report.document.format.toUpperCase()}</dd>
					<dt>{t('method.logPages')}</dt>
					<dd>{report.document.pages || '—'}</dd>
					<dt>{t('method.logChars')}</dt>
					<dd class="mono">{report.document.chars.toLocaleString(locale())}</dd>
					<dt>{t('method.logQuality')}</dt>
					<dd class="mono">
						{Math.round(report.document.quality.score * 100)}/100
						({confidenceShort(report.document.quality.level)})
					</dd>
					<dt>{t('method.logOperators')}</dt>
					<dd class="mono">{report.document.quality.operatorCorruptions}</dd>
					<dt>{t('method.logBlocks')}</dt>
					<dd class="mono">{doc.blocks.length}</dd>
					<dt>{t('method.logCreated')}</dt>
					<dd class="mono">{new Date(report.generatedAt).toLocaleString(locale())}</dd>
				</dl>
				{#if report.document.quality.notes.length}
					<p class="small muted">{t('method.logNotes')}</p>
					<ul class="small notes">
						{#each report.document.quality.notes as n (n)}<li>{qualityNote(n, i18n.lang)}</li>{/each}
					</ul>
				{/if}
			</div>
		{/if}
	</section>

	<IssueAppendix {report} />
</div>

<style>
	.method {
		display: grid;
		gap: 1rem;
	}
	.lead {
		padding: 1.4rem 1.5rem;
		background:
			radial-gradient(100% 140% at 0% 0%, rgb(227 241 238 / 0.6), transparent 60%),
			var(--card);
	}
	.lead h2 {
		margin: 0 0 0.5rem;
	}
	.lead p {
		max-width: 68ch;
		color: var(--ink-2);
		margin: 0 0 1rem;
	}
	.pipeline {
		display: flex;
		gap: 0.5rem;
		flex-wrap: wrap;
		align-items: center;
		font-size: 0.76rem;
		color: var(--muted);
	}
	.pipeline span:not([aria-hidden]) {
		background: #fff;
		border: 1px solid var(--line);
		border-radius: 99px;
		padding: 0.15rem 0.6rem;
		color: var(--ink-2);
	}
	.grid2 {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 1rem;
	}
	@media (max-width: 900px) {
		.grid2 {
			grid-template-columns: 1fr;
		}
	}
	.panel {
		padding: 1.15rem 1.3rem;
	}
	.panel h3 {
		font-family: var(--font-ui);
		font-size: 0.92rem;
		font-weight: 650;
		margin: 0 0 0.6rem;
	}
	.panel ul {
		margin: 0 0 0.6rem;
		padding-left: 1.1rem;
		display: grid;
		gap: 0.35rem;
	}
	.panel p {
		margin: 0 0 0.5rem;
	}
	.conf-table {
		width: 100%;
		border-collapse: collapse;
		font-size: 0.78rem;
		margin: 0.5rem 0;
	}
	.conf-table th {
		text-align: left;
		color: var(--muted);
		font-weight: 600;
		padding: 0.4rem 0.5rem;
		border-bottom: 1px solid var(--line);
		font-size: 0.7rem;
		text-transform: uppercase;
		letter-spacing: 0.04em;
	}
	.conf-table td {
		padding: 0.4rem 0.5rem;
		border-bottom: 1px solid var(--line);
	}
	.two-col {
		display: grid;
		grid-template-columns: 1.5fr 1fr;
		gap: 1.2rem;
	}
	@media (max-width: 720px) {
		.two-col {
			grid-template-columns: 1fr;
		}
	}
	.formulas {
		background: var(--paper-2);
		border-radius: 9px;
		padding: 0.7rem 0.9rem;
		font-size: 0.78rem;
		color: var(--ink-2);
		align-self: start;
	}
	.formulas p {
		margin: 0 0 0.3rem;
	}
	.formulas p:last-child {
		margin: 0;
	}
	.weights {
		display: grid;
		gap: 0.4rem;
		margin: 0.6rem 0;
	}
	.weight {
		display: grid;
		grid-template-columns: 170px 1fr 44px;
		gap: 0.6rem;
		align-items: center;
		font-size: 0.8rem;
	}
	.w-track {
		height: 6px;
		background: var(--paper-2);
		border-radius: 99px;
		overflow: hidden;
	}
	.w-fill {
		display: block;
		height: 100%;
		background: var(--accent);
		border-radius: inherit;
	}
	.limits {
		color: var(--ink-2);
	}
	.log-toggle {
		display: flex;
		width: 100%;
		align-items: center;
		gap: 0.6rem;
		background: none;
		border: 0;
		padding: 0;
		font-size: 0.88rem;
		font-weight: 600;
		cursor: pointer;
		text-align: left;
	}
	.log-toggle .mono {
		margin-left: auto;
		font-weight: 400;
	}
	.log {
		margin-top: 0.8rem;
		border-top: 1px solid var(--line);
		padding-top: 0.8rem;
	}
	dl {
		display: grid;
		grid-template-columns: 160px 1fr;
		gap: 0.25rem 0.8rem;
		margin: 0;
		font-size: 0.8rem;
	}
	dt {
		color: var(--muted);
	}
	dd {
		margin: 0;
	}
	.notes {
		margin: 0.4rem 0 0;
		color: var(--muted);
	}
</style>
